/**
 * Sentry initialization for the React frontend.
 *
 * Imported as the FIRST line in index.js so global error handlers attach before
 * React mounts. The whole file is a no-op when REACT_APP_SENTRY_DSN is empty —
 * that's how we keep tests/preview from sending events to production.
 */
import * as Sentry from "@sentry/react";

const DSN = (process.env.REACT_APP_SENTRY_DSN || "").trim();
// Environment: explicit env var wins; otherwise auto-detect from the hostname so
// production errors are NEVER mislabeled as "preview". Preview URLs contain
// "preview.emergentagent"; everything else (becomeresumeready.com, *.emergent.host) is production.
const _autoEnv = (() => {
  try {
    const host = (typeof window !== "undefined" && window.location && window.location.hostname) || "";
    if (host.includes("preview.emergentagent") || host === "localhost" || host === "127.0.0.1") return "preview";
    return "production";
  } catch (_e) {
    return "preview";
  }
})();
const ENV = (process.env.REACT_APP_SENTRY_ENV || _autoEnv).trim();
const TRACES_SAMPLE_RATE = parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0.05");

const SENSITIVE_HEADER_HINTS = ["authorization", "x-paystack", "api-key", "cookie"];
const SENSITIVE_FIELD_HINTS = ["token", "jwt", "paystack", "password", "secret", "api_key"];

const matchesHint = (key, hints) => {
  const lower = String(key || "").toLowerCase();
  return hints.some((h) => lower.includes(h));
};

const scrub = (event) => {
  try {
    // User context — never send emails
    if (event.user && event.user.email) {
      delete event.user.email;
    }

    const req = event.request || {};
    if (req.headers && typeof req.headers === "object") {
      for (const k of Object.keys(req.headers)) {
        if (matchesHint(k, SENSITIVE_HEADER_HINTS)) {
          req.headers[k] = "[Filtered]";
        }
      }
    }
    if (req.data && typeof req.data === "object" && !Array.isArray(req.data)) {
      for (const k of Object.keys(req.data)) {
        if (matchesHint(k, SENSITIVE_FIELD_HINTS)) {
          req.data[k] = "[Filtered]";
        }
      }
    }

    // Breadcrumbs — scrub the data payload of each
    if (Array.isArray(event.breadcrumbs)) {
      event.breadcrumbs = event.breadcrumbs.map((crumb) => {
        if (crumb && crumb.data && typeof crumb.data === "object") {
          const cleaned = { ...crumb.data };
          for (const k of Object.keys(cleaned)) {
            if (matchesHint(k, [...SENSITIVE_FIELD_HINTS, ...SENSITIVE_HEADER_HINTS])) {
              cleaned[k] = "[Filtered]";
            }
          }
          return { ...crumb, data: cleaned };
        }
        return crumb;
      });
    }
  } catch (_e) {
    // Never let the scrubber crash event delivery
    if (process.env.NODE_ENV === 'development') {
      console.error('[Sentry scrubber error]', _e);
    }
  }
  return event;
};

export const SENTRY_ENABLED = Boolean(DSN);

if (DSN) {
  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.httpClientIntegration({ failedRequestStatusCodes: [[500, 599]] }),
    ],
    sampleRate: 1.0,
    tracesSampleRate: TRACES_SAMPLE_RATE,
    sendDefaultPii: false,
    beforeSend: scrub,
    // Keep noisy / non-actionable errors out of the budget
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection captured",
      "Network request failed",
    ],
  });
  // eslint-disable-next-line no-console
  console.info(`[Sentry] frontend monitoring active (env=${ENV})`);
}

export { Sentry };
